import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { authAPI } from '../services/(adminapi)/authAPI'
import { clearAuth } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import { 
  fetchBugReports, 
  updateBugReportStatus,
  clearError,
  clearSuccess 
} from '../store/slices/bugSlice'

const BugReports = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user, business } = useSelector((state) => state.auth)
  const { bugReports: rawBugReports, loading, error, success } = useSelector((state) => state.bug)

  const [isSigningOut, setIsSigningOut] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [bugReports, setBugReports] = useState([])

  // Transform bug reports data
  useEffect(() => {
    if (rawBugReports && rawBugReports.length > 0) {
      const transformedData = rawBugReports.map(report => ({
        id: report.id,
        title: report.title || 'Untitled',
        type: report.feedback_type || 'Bug Report',
        reportedAt: report.created_at ? new Date(report.created_at).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit'
        }) : 'N/A',
        status: report.status === 'open' ? 'Open' : report.status === 'resolved' ? 'Resolved' : report.status || 'Open'
      }))
      setBugReports(transformedData)
    } else {
      setBugReports([])
    }
  }, [rawBugReports])

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess())
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [success, dispatch])

  // Fetch bug reports when tab changes
  useEffect(() => {
    const params = {}
    if (activeTab !== 'all') {
      params.status = activeTab === 'open' ? 'open' : 'resolved'
    }
    dispatch(fetchBugReports({ params, businessId: business?.id || business?._id }))
  }, [activeTab, dispatch, business])

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true)
      await authAPI.signOut()
      dispatch(clearAuth())
      navigate('/signin')
    } catch (error) {
      console.error('Sign out error:', error)
    } finally {
      setIsSigningOut(false)
    }
  }

  const handleMarkAsResolved = async (bugId) => {
    dispatch(updateBugReportStatus({ bugId, status: 'resolved' })).then(() => {
      // Refresh the bug reports list
      const params = {}
      if (activeTab !== 'all') {
        params.status = activeTab === 'open' ? 'open' : 'resolved'
      }
      dispatch(fetchBugReports({ params, businessId: business?.id || business?._id }))
    })
  }

  const filteredReports = bugReports.filter(report => {
    // Apply search filter
    if (searchTerm && !report.title?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false
    }
    
    // Apply status filter
    if (activeTab === 'all') return true
    if (activeTab === 'open') return report.status === 'Open' || report.status === 'Active'
    if (activeTab === 'resolved') return report.status === 'Resolved'
    return true
  })

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }

  const mainContentStyle = {
    flex: 1,
    padding: '24px',
    marginLeft: '280px'
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0
  }

  const searchContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const searchInputStyle = {
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    width: '200px',
    outline: 'none'
  }

  const tabsStyle = {
    display: 'flex',
    gap: '0',
    marginBottom: '24px',
    borderBottom: '1px solid #e5e7eb'
  }

  const tabStyle = {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    color: '#6b7280',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s'
  }

  const activeTabStyle = {
    ...tabStyle,
    color: '#3b82f6',
    borderBottomColor: '#3b82f6'
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  }

  const thStyle = {
    backgroundColor: '#f9fafb',
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '1px solid #e5e7eb'
  }

  const tdStyle = {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #f3f4f6'
  }

  const statusStyle = {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'uppercase'
  }

  const openStatusStyle = {
    ...statusStyle,
    backgroundColor: '#fef3c7',
    color: '#92400e'
  }

  const activeStatusStyle = {
    ...statusStyle,
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  }

  const resolvedStatusStyle = {
    ...statusStyle,
    backgroundColor: '#d1fae5',
    color: '#065f46'
  }

  const actionButtonStyle = {
    color: '#3b82f6',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none',
    background: 'none'
  }

  const emptyContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center'
  }

  const emptyTextStyle = {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0
  }

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Open':
        return openStatusStyle
      case 'Active':
        return activeStatusStyle
      case 'Resolved':
        return resolvedStatusStyle
      default:
        return statusStyle
    }
  }

  if (loading) {
    return (
      <div style={containerStyle}>
        <Sidebar />
        <div style={mainContentStyle}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading bug reports...</div>
          </div>
        </div>
      </div>
    )
  }

  if (error && bugReports.length === 0) {
    return (
      <div style={containerStyle}>
        <Sidebar />
        <div style={mainContentStyle}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <div style={{ fontSize: '16px', color: '#ef4444' }}>{error}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <Sidebar />
      <div style={mainContentStyle}>
        {/* Success/Error Messages */}
        {success && (
          <div style={{
            backgroundColor: '#D1FAE5',
            color: '#065F46',
            padding: '12px 16px',
            borderRadius: '8px',
            margin: '16px 0',
            border: '1px solid #A7F3D0',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            ✅ {success}
          </div>
        )}
        {error && (
          <div style={{
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            padding: '12px 16px',
            borderRadius: '8px',
            margin: '16px 0',
            border: '1px solid #FCA5A5',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            ❌ {error}
          </div>
        )}

        <PageTopBar
          title="Bug Reports"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />
        <div style={{ marginBottom: '24px' }}>
          <div style={searchContainerStyle}>
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={searchInputStyle}
            />
          </div>
        </div>

        <div style={tabsStyle}>
          <button
            style={activeTab === 'all' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('all')}
          >
            All
          </button>
          <button
            style={activeTab === 'open' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('open')}
          >
            Open
          </button>
          <button
            style={activeTab === 'resolved' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('resolved')}
          >
            Resolved
          </button>
        </div>

        {bugReports.length === 0 ? (
          <div style={emptyContainerStyle}>
            <p style={emptyTextStyle}>No bug reports found. Bug reports will appear here when users flag issues or provide feedback.</p>
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Bug ID</th>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Reported At</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ ...tdStyle, textAlign: 'center', padding: '40px' }}>
                    No bug reports match your search or filter criteria
                  </td>
                </tr>
              ) : (
                filteredReports.map((report) => (
                  <tr key={report.id}>
                    <td style={tdStyle}>#{report.id?.toString().slice(-8) || 'N/A'}</td>
                    <td style={tdStyle}>{report.title}</td>
                    <td style={tdStyle}>{report.type}</td>
                    <td style={tdStyle}>{report.reportedAt}</td>
                    <td style={tdStyle}>
                      <span style={getStatusStyle(report.status)}>
                        {report.status}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {report.status !== 'Resolved' && (
                        <button
                          style={actionButtonStyle}
                          onClick={() => handleMarkAsResolved(report.id)}
                        >
                          Mark as Resolved
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default BugReports
